import React, { useState } from "react";
import "../../../../styles/gridStyle.css";
import { IPetsDashboardResponseModel } from "../../../../typings/models/PetsDashboardResponseModel";
import PetsRegGridRow from "./PetsRegGridRow";
import Loader from "../../../../components/molecules/Loader";

interface RegGridProps {
  data: any[];
  isLoading: boolean;
  headerColumns: any[];
  setData: React.Dispatch<React.SetStateAction<any[]>>;
}

const PetsRegGrid: React.FC<RegGridProps> = ({
  data,
  isLoading,
  headerColumns,
  setData,
}) => {
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [currentPage, setCurrentPage] = useState(1);
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const itemsPerPage = 10;
  const maxPagesToShow = 5;

  const handleRemoveItem = (itemToRemove: any) => {
    const newData = data.filter((item) => item !== itemToRemove);
    setData(newData);
  };

  // Filter the data based on the search query
  const filteredData =
    data.length > 0
      ? data.filter((item) =>
          Object.values(item).some((value) =>
            value?.toString().toLowerCase().includes(searchQuery?.toLowerCase())
          )
        )
      : [];

  // Function to perform sorting based on the current sort settings
  const sortedData = filteredData.sort((a, b) => {
    if (sortColumn === null) return 0;

    const aValue = a[sortColumn];
    const bValue = b[sortColumn];

    if (aValue < bValue) {
      return sortDirection === "asc" ? -1 : 1;
    } else if (aValue > bValue) {
      return sortDirection === "asc" ? 1 : -1;
    } else {
      return 0;
    }
  });

  // Calculate the total number of pages
  const totalPages = Math.ceil(sortedData.length / itemsPerPage);

  // Get the data to display on the current page
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const dataToShow = sortedData.slice(startIndex, endIndex);
  const [activeButton, setActiveButton] = useState<string>(""); // added for edit form

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1); // Reset to the first page when searching
  };

  const handlePagination = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleSort = (column: string) => {
    if (column === sortColumn) {
      // If the same column is clicked again, toggle the sort direction
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // If a new column is clicked, set the new column and default to ascending sort
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const renderPageButtons = () => {
    const pageButtons = [];
    let startPage = 1;
    let endPage = totalPages;

    if (totalPages > maxPagesToShow) {
      const halfMaxPages = Math.floor(maxPagesToShow / 2);
      startPage = Math.max(currentPage - halfMaxPages, 1);
      endPage = startPage + maxPagesToShow - 1;

      if (endPage > totalPages) {
        endPage = totalPages;
        startPage = endPage - maxPagesToShow + 1;
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pageButtons.push(
        <li
          key={i}
          className={`page-item ${currentPage === i ? "active" : ""}`}
        >
          <button className="page-link" onClick={() => handlePagination(i)}>
            {i}
          </button>
        </li>
      );
    }

    if (totalPages > maxPagesToShow && endPage < totalPages) {
      pageButtons.push(
        <li key="separator" className="page-item disabled">
          <button className="page-link">...</button>
        </li>
      );
      pageButtons.push(
        <li
          key={totalPages}
          className={`page-item ${currentPage === totalPages ? "active" : ""}`}
        >
          <button
            className="page-link"
            onClick={() => handlePagination(totalPages)}
          >
            {totalPages}
          </button>
        </li>
      );
    }

    return pageButtons;
  };
  return (
    <div className="container">
      <input
        type="text"
        placeholder="Search..."
        value={searchQuery}
        onChange={handleSearch}
        className="form-control mb-3"
      />
      <div className="table-responsive">
        <table className="table table-bordered table-hover">
          {/* Table header */}
          <thead>
            <tr>
              {headerColumns.map((column, index) => (
                <th
                  key={index}
                  className="grid-header"
                  onClick={() => handleSort(column.sortKey)}
                >
                  {column.columnName}&nbsp;
                  <span>{sortDirection === "asc" ? "▲" : "▼"}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {dataToShow.map((item) => (
              <PetsRegGridRow
                key={item.PetsRegistrationId}
                data={item}
                onRemoveItem={handleRemoveItem}
                activeButton={activeButton} // added for edit form
              />
            ))}
          </tbody>
        </table>
      </div>
      {isLoading && <Loader />}

      {/* Pagination */}
      <nav aria-label="Page navigation example">
        <ul className="pagination">
          <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
            <button
              className="page-link"
              onClick={() => handlePagination(currentPage - 1)}
            >
              Previous
            </button>
          </li>
          {renderPageButtons()}
          <li
            className={`page-item ${
              currentPage === totalPages ? "disabled" : ""
            }`}
          >
            <button
              className="page-link"
              onClick={() => handlePagination(currentPage + 1)}
            >
              Next
            </button>
          </li>
        </ul>
      </nav>
    </div>
  );
};

export default PetsRegGrid;
